import cn from 'classnames';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import appConfig from '../../../config/appConfig';
import AMPLITUDE_EVENT_VALUES from '../../../consts/amplitude/amplitudeEventValues';
import * as localizationKeys from '../../../consts/localization/localizationKeys';
import amplitudeKodakTracking from '../../../services/amplitude/amplitudeKodakTracking';
import {getLocalizedStrings} from '../../../services/localization/localizationService';
import appRouterService from '../../../services/route/appRouterService';
import {selectIsDeviceReady} from '../../../state/selectors/iotDevice';
import elementsTesIds from '../../../test/consts/elementsTesIds';
import helpers from '../../../utils/helpers';
import {AppButton} from '../../Button';
import InternetAndDeviceTrackButton from '../../Button/ConnectionAndDeviceTrackButton/InternetAndDeviceTrackButton';
import {Article} from '../../DataDisplay';
import {LeviaIcon} from '../../Icons';
import {getLeviaLearnMoreData} from './leviaDataService';
import LeviaLearnMoreItem from './LeviaLearnMoreItem';

const LeviaLearnMoreArticle = ({onCancel, isCheckCompatibility, isUsage}) => {
    const isDeviceReady = useSelector(selectIsDeviceReady);
    const localizedStrings = getLocalizedStrings();
    const data = getLeviaLearnMoreData();
    const isAnyItem = data.some((x) => x.titleKey);
    const learnMoreUrl = appConfig.getLeviaLearnMoreUrlFormatted();

    const onCheckCompatibilityClick = () => {
        appRouterService.forwardToUpdateCheckingPage();
        helpers.runFunction(onCancel);
    };

    const onLearnMoreClick = () => {
        const learnMoreSource = getLearnMoreSource({isCheckCompatibility, isUsage});

        amplitudeKodakTracking.trackLearnMoreClick(learnMoreSource);
    };

    useEffect(() => {
        if (isUsage) return;

        if (learnMoreUrl && !isCheckCompatibility) {
            amplitudeKodakTracking.trackLeviaLearnMoreScreen();
        }

        if (isCheckCompatibility) {
            amplitudeKodakTracking.trackCheckCompatibilityScreen();
        }
    }, []);

    return (
        <Article
            icon={<LeviaIcon className='h-16 w-16' />}
            titleClassName='text-28 mt-4'
            titleKey={localizationKeys.LEVIA_LEARN_MORE_TITLE}
            descriptionKey={localizationKeys.LEVIA_LEARN_MORE_DESCRIPTION}
        >
            {isAnyItem && (
                <p className='mt-8 text-20 font-bold'>{localizedStrings[localizationKeys.LEVIA_LEARN_MORE_ABOUT]}</p>
            )}
            <ul className='mt-4 flex max-w-[356px] flex-col items-start gap-4'>
                {data.map((item, index) => (
                    <LeviaLearnMoreItem key={index} item={item} />
                ))}
            </ul>
            {isCheckCompatibility && (
                <InternetAndDeviceTrackButton
                    isDeviceReady={isDeviceReady}
                    button={AppButton}
                    className='mt-8 max-w-[312px]'
                    primary={isCheckCompatibility}
                    secondary={!isCheckCompatibility}
                    light
                    onClick={onCheckCompatibilityClick}
                    testId={elementsTesIds.ONBOARD_LEVIA_BTN_CHECK}
                >
                    {localizedStrings[localizationKeys.LEVIA_LEARN_MORE_CHECK_DEVICE_COMPATIBILITY_BUTTON]}
                </InternetAndDeviceTrackButton>
            )}
            {learnMoreUrl && (
                <AppButton
                    className={cn('max-w-[312px]', isCheckCompatibility ? 'mt-3' : 'mt-8')}
                    primary={!isCheckCompatibility}
                    secondary={isCheckCompatibility}
                    light
                    to={learnMoreUrl}
                    onClick={onLearnMoreClick}
                    isExternalLink
                    isTargetBlank
                    testId={elementsTesIds.ONBOARD_LEVIA_BTN_LEARN_MORE}
                >
                    {localizedStrings[localizationKeys.LEVIA_LEARN_MORE_BUTTON]}
                </AppButton>
            )}
            {onCancel && (
                <AppButton
                    tertiary
                    className='mt-3 text-center md:mt-4'
                    testId={elementsTesIds.ONBOARD_LEVIA_BTN_NOT_NOW}
                    onClick={onCancel}
                >
                    {localizedStrings[localizationKeys.LEVIA_LEARN_MORE_SKIP_BUTTON]}
                </AppButton>
            )}
        </Article>
    );
};

export default LeviaLearnMoreArticle;

const getLearnMoreSource = ({isCheckCompatibility, isUsage}) => {
    let learnMoreSource;

    if (isCheckCompatibility) {
        if (isUsage) {
            learnMoreSource = AMPLITUDE_EVENT_VALUES.USAGE_CHECK_COMPATIBILITY;
        } else {
            learnMoreSource = AMPLITUDE_EVENT_VALUES.LEVIA_CHECK_COMPATIBILITY;
        }
    } else {
        if (isUsage) {
            learnMoreSource = AMPLITUDE_EVENT_VALUES.USAGE_LEARN_MORE;
        } else {
            learnMoreSource = AMPLITUDE_EVENT_VALUES.LEVIA_LEARN_MORE;
        }
    }

    return learnMoreSource;
};
